export type RelatedHub = {
  hub_target_entity_rdv_id: number | null;
  hub_target_entity_rdv_name: string | null;
  hub_role?: string | null;
};

export type RelatedSat = {
  sat_target_entity_rdv_id: number | null;
  sat_target_entity_rdv_name: string | null;
  sat_role?: string | null;
  is_active?: boolean | null;
};

export type DataVaultNode = {
  source_entity_id: number | null;
  source_entity_name: string | null;
  data_source_name: string | null;
  data_source_type: string | null;
  target_entity_rdv_id: number | null;
  target_entity_rdv_name: string | null;
  target_schema: string | null;
  ensemble: string | null;
  subject_area: string | null;
  source_filter: string | null;
  table_construct: string | null;
  skip_metadata_tests?: boolean | null;
  include_dq_test?: boolean | null;
  generate_model_entity?: boolean | null;
  dq_source_filter?: string | null;
  dw_target_types?: string | null;
  canvas_x?: number | null;
  canvas_y?: number | null;
  linked_hubs?: RelatedHub[];
  sat_hubs?: RelatedSat[];
  synthetic_cluster_id?: number;
};

export type LayoutOptions = {
  nodeWidth?: number;
  nodeHeight?: number;
  spacingX?: number;
  spacingY?: number;
  clusterGapX?: number;
  clusterGapY?: number;
  clustersPerRow?: number;
  originX?: number;
  originY?: number;
};

const DEFAULTS = {
  nodeWidth: 240,
  nodeHeight: 90,
  spacingX: 60,
  spacingY: 60,
  clusterGapX: 260,
  clusterGapY: 260,
  clustersPerRow: 4,
  originX: 0,
  originY: 0
};

const normalizeConstruct = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

const isHub = (node: DataVaultNode) => normalizeConstruct(node.table_construct).startsWith("hub");
const isLink = (node: DataVaultNode) => normalizeConstruct(node.table_construct).startsWith("link");
const isSat = (node: DataVaultNode) => normalizeConstruct(node.table_construct).startsWith("sat");

// Within one BAND (a horizontal run of hubs), rows are: links on top, hubs in the middle,
// satellites stacked beneath. Large clusters wrap into multiple bands so the model gains vertical
// depth instead of stretching into one long horizontal line.
const BAND_ROW_LINK = 0;
const BAND_ROW_HUB = 2;
const BAND_ROW_SAT_START = 3;
// Max hubs in a single horizontal band before wrapping to the next band. Keeps the model roughly
// landscape (a touch wider than tall) rather than one endless line.
const HUBS_PER_BAND = 8;
// Extra empty rows between the bottom of one band's satellites and the next band's links.
const BAND_VERTICAL_GAP = 2;

function keyForNode(node: DataVaultNode) {
  return `${node.synthetic_cluster_id}|${node.target_entity_rdv_id ?? "null"}|${node.source_entity_id ?? "null"}|${node.target_entity_rdv_name ?? ""}`;
}

/**
 * Layered, edge-aware placement for one connected cluster.
 *
 * 1. HUBS form a horizontal spine, ORDERED to reduce link-edge crossings (barycenter of the
 *    columns of the hubs each hub shares a link with), not alphabetically.
 * 2. SATELLITES stack in a clean vertical column directly beneath their parent hub
 *    (resolved from sat_hubs / parentHubId; ensemble + name-convention as fallback).
 * 3. LINKS sit one rank above the spine, centered over the average column of their connected hubs.
 * 4. ORPHANS (hubs/sats without resolvable membership, manual, other) tile in a band below.
 *
 * Columns are integer grid positions multiplied by stepX/stepY at the end.
 */
function buildClusterLocalLayout(nodes: DataVaultNode[], stepX: number, stepY: number) {
  const hubs = nodes.filter(isHub);
  const links = nodes.filter(isLink);
  const sats = nodes.filter(isSat);
  const others = nodes.filter((n) => !isHub(n) && !isLink(n) && !isSat(n));

  const slots = new Map<string, { col: number; row: number }>();

  // ---- index hubs by id and name for membership resolution ----
  const hubByIdKey = new Map<string, DataVaultNode>();
  const hubByNameKey = new Map<string, DataVaultNode>();
  hubs.forEach((hub) => {
    if (hub.target_entity_rdv_id != null) hubByIdKey.set(`id:${hub.target_entity_rdv_id}`, hub);
    if (hub.target_entity_rdv_name) hubByNameKey.set(`name:${hub.target_entity_rdv_name}`, hub);
  });
  const resolveHub = (id: number | null | undefined, name: string | null | undefined) =>
    (id != null ? hubByIdKey.get(`id:${id}`) : undefined) ??
    (name ? hubByNameKey.get(`name:${name}`) : undefined) ??
    null;

  // ---- hub adjacency via links (for crossing-reducing order) ----
  const hubIndex = new Map<DataVaultNode, number>();
  hubs.forEach((hub, i) => hubIndex.set(hub, i));
  const hubNeighbors = new Map<number, Set<number>>();
  hubs.forEach((_, i) => hubNeighbors.set(i, new Set<number>()));
  links.forEach((link) => {
    const connected = (link.linked_hubs ?? [])
      .map((h) => resolveHub(h.hub_target_entity_rdv_id, h.hub_target_entity_rdv_name))
      .filter((h): h is DataVaultNode => h != null)
      .map((h) => hubIndex.get(h)!)
      .filter((idx) => idx != null);
    connected.forEach((a) => connected.forEach((b) => { if (a !== b) hubNeighbors.get(a)!.add(b); }));
  });

  // ---- order hubs: seed alphabetically, then one barycenter sweep to cluster linked hubs ----
  let hubOrder = hubs
    .map((hub, i) => ({ hub, i }))
    .sort((a, b) =>
      (a.hub.target_entity_rdv_name ?? "").localeCompare(b.hub.target_entity_rdv_name ?? "")
    )
    .map((x) => x.i);
  const positionOf = new Map<number, number>();
  hubOrder.forEach((hubI, pos) => positionOf.set(hubI, pos));
  const barycenter = (hubI: number) => {
    const nbrs = Array.from(hubNeighbors.get(hubI) ?? []);
    if (!nbrs.length) return positionOf.get(hubI)!;
    return nbrs.reduce((acc, n) => acc + (positionOf.get(n) ?? 0), 0) / nbrs.length;
  };
  for (let sweep = 0; sweep < 4; sweep += 1) {
    hubOrder = [...hubOrder].sort((a, b) => barycenter(a) - barycenter(b));
    hubOrder.forEach((hubI, pos) => positionOf.set(hubI, pos));
  }

  // ---- resolve sat -> hub BEFORE placing hubs, so each band can reserve vertical room for the
  // tallest satellite stack it contains (bands must not overlap vertically) ----
  const satParentByNodeKey0 = new Map<string, DataVaultNode>();
  hubs.forEach((hub) => {
    (hub.sat_hubs ?? []).forEach((s) => {
      if (s.sat_target_entity_rdv_id != null) satParentByNodeKey0.set(`id:${s.sat_target_entity_rdv_id}`, hub);
      if (s.sat_target_entity_rdv_name) satParentByNodeKey0.set(`name:${s.sat_target_entity_rdv_name}`, hub);
    });
  });
  const stemHub = (sat: DataVaultNode): DataVaultNode | null => {
    const nm = sat.target_entity_rdv_name ?? "";
    for (const cand of [
      nm.replace(/_main_[a-z0-9]+_s$/i, "_h"),
      nm.replace(/_main_s$/i, "_h"),
      nm.replace(/_[a-z0-9]+_s$/i, "_h"),
      nm.replace(/_s$/i, "_h")
    ]) {
      const hit = hubByNameKey.get(`name:${cand}`);
      if (hit) return hit;
    }
    return null;
  };
  const parentOf = (sat: DataVaultNode): DataVaultNode | null =>
    (sat.target_entity_rdv_id != null ? satParentByNodeKey0.get(`id:${sat.target_entity_rdv_id}`) : undefined) ??
    (sat.target_entity_rdv_name ? satParentByNodeKey0.get(`name:${sat.target_entity_rdv_name}`) : undefined) ??
    stemHub(sat) ??
    (sat.ensemble ? hubs.find((h) => h.ensemble === sat.ensemble) ?? null : null) ??
    null;
  const satCountByHub = new Map<DataVaultNode, number>();
  sats.forEach((sat) => {
    const p = parentOf(sat);
    if (p) satCountByHub.set(p, (satCountByHub.get(p) ?? 0) + 1);
  });

  // ---- place hubs in BANDS (wrap the spine), each band tall enough for its satellites ----
  // hubCol = column within the whole layout; hubRow = absolute row of the hub.
  const hubCol = new Map<DataVaultNode, number>();
  const hubRow = new Map<DataVaultNode, number>();
  let bandTopRow = 0;
  for (let start = 0; start < hubOrder.length; start += HUBS_PER_BAND) {
    const bandHubIs = hubOrder.slice(start, start + HUBS_PER_BAND);
    const bandHubs = bandHubIs.map((i) => hubs[i]);
    const hubAbsRow = bandTopRow + BAND_ROW_HUB;
    bandHubs.forEach((hub, colInBand) => {
      slots.set(keyForNode(hub), { col: colInBand, row: hubAbsRow });
      hubCol.set(hub, colInBand);
      hubRow.set(hub, hubAbsRow);
    });
    const tallestSatStack = Math.max(0, ...bandHubs.map((h) => satCountByHub.get(h) ?? 0));
    // band consumes: link row(s) .. hub row .. sat stack; advance past all of it + a gap
    bandTopRow = bandTopRow + BAND_ROW_SAT_START + tallestSatStack + BAND_VERTICAL_GAP;
  }

  // ---- satellites: stack beneath their parent hub, in that hub's band ----
  const satsByHub = new Map<DataVaultNode, DataVaultNode[]>();
  const orphanSats: DataVaultNode[] = [];
  sats.forEach((sat) => {
    const parent = parentOf(sat);
    if (parent) {
      satsByHub.set(parent, [...(satsByHub.get(parent) ?? []), sat]);
    } else {
      orphanSats.push(sat);
    }
  });
  // each hub's satellites stack straight down from the hub's row, in the hub's column
  satsByHub.forEach((hubSats, hub) => {
    const col = hubCol.get(hub) ?? 0;
    const baseRow = (hubRow.get(hub) ?? BAND_ROW_HUB) + 1;
    [...hubSats]
      .sort((a, b) => (a.target_entity_rdv_name ?? "").localeCompare(b.target_entity_rdv_name ?? ""))
      .forEach((sat, i) => slots.set(keyForNode(sat), { col, row: baseRow + i }));
  });

  // ---- links: one rank above the band of their connected hubs, centered over them ----
  // de-overlap per (row) so links in different bands don't collide with each other.
  const usedLinkColsByRow = new Map<number, Set<number>>();
  const reserveLinkCol = (row: number, preferred: number) => {
    if (!usedLinkColsByRow.has(row)) usedLinkColsByRow.set(row, new Set<number>());
    const used = usedLinkColsByRow.get(row)!;
    let col = preferred;
    for (let r = 0; used.has(col) && r < 200; r += 1) {
      col = preferred + (r % 2 === 0 ? r / 2 + 1 : -(Math.floor(r / 2) + 1));
    }
    used.add(col);
    return col;
  };
  links
    .map((link) => {
      const connectedHubs = (link.linked_hubs ?? [])
        .map((h) => resolveHub(h.hub_target_entity_rdv_id, h.hub_target_entity_rdv_name))
        .filter((h): h is DataVaultNode => h != null);
      const cols = connectedHubs.map((h) => hubCol.get(h)).filter((c): c is number => typeof c === "number");
      const rows = connectedHubs.map((h) => hubRow.get(h)).filter((r): r is number => typeof r === "number");
      const center = cols.length ? cols.reduce((a, c) => a + c, 0) / cols.length : null;
      // sit just above the topmost connected hub's band; default to the first band's link row
      const linkRow = rows.length ? Math.min(...rows) - (BAND_ROW_HUB - BAND_ROW_LINK) : BAND_ROW_LINK;
      return { link, center, linkRow };
    })
    .sort((a, b) => (a.center ?? 1e9) - (b.center ?? 1e9))
    .forEach(({ link, center, linkRow }, idx) => {
      const preferred = center != null ? Math.round(center) : idx;
      slots.set(keyForNode(link), { col: reserveLinkCol(linkRow, preferred), row: linkRow });
    });

  // ---- orphan sats + manual/other nodes: tile in a band below the deepest sat ----
  const deepestSatRow = Array.from(slots.values()).reduce((m, s) => Math.max(m, s.row), BAND_ROW_SAT_START);
  const bandRow = deepestSatRow + 2;
  const bandWidth = Math.max(1, hubs.length || 4);
  [...orphanSats, ...others].forEach((node, i) => {
    slots.set(keyForNode(node), { col: i % bandWidth, row: bandRow + Math.floor(i / bandWidth) });
  });

  // ---- normalize to pixel coordinates ----
  const allSlots = Array.from(slots.values());
  const minCol = Math.min(...allSlots.map((s) => s.col), 0);
  const maxCol = Math.max(...allSlots.map((s) => s.col), 0);
  const minRow = Math.min(...allSlots.map((s) => s.row), 0);
  const maxRow = Math.max(...allSlots.map((s) => s.row), 0);

  const normalizedSlots = new Map<string, { x: number; y: number }>();
  slots.forEach((slot, key) => {
    normalizedSlots.set(key, { x: (slot.col - minCol) * stepX, y: (slot.row - minRow) * stepY });
  });

  return {
    slots: normalizedSlots,
    width: Math.max(1, maxCol - minCol + 1) * stepX,
    height: Math.max(1, maxRow - minRow + 1) * stepY,
    keyFor: keyForNode
  };
}

export function layoutClusters(nodes: DataVaultNode[], options: LayoutOptions = {}): DataVaultNode[] {
  const cfg = { ...DEFAULTS, ...options };
  const stepX = cfg.nodeWidth + cfg.spacingX;
  const stepY = cfg.nodeHeight + cfg.spacingY;

  const clusters = new Map<number, DataVaultNode[]>();
  nodes.forEach((node) => {
    const clusterId = node.synthetic_cluster_id ?? 1;
    clusters.set(clusterId, [...(clusters.get(clusterId) ?? []), node]);
  });

  const clusterIds = Array.from(clusters.keys()).sort((a, b) => a - b);
  const clusterLayouts = new Map<number, ReturnType<typeof buildClusterLocalLayout>>();

  let maxClusterWidth = stepX;
  let maxClusterHeight = stepY;
  clusterIds.forEach((clusterId) => {
    const clusterNodes = clusters.get(clusterId) ?? [];
    const local = buildClusterLocalLayout(clusterNodes, stepX, stepY);
    clusterLayouts.set(clusterId, local);
    maxClusterWidth = Math.max(maxClusterWidth, local.width);
    maxClusterHeight = Math.max(maxClusterHeight, local.height);
  });

  const positioned = nodes.map((node) => ({ ...node }));
  const positionByNodeKey = new Map<string, { x: number; y: number }>();

  // Tight SHELF packing: lay clusters left-to-right on a shelf until the row exceeds a target
  // width, then wrap to a new shelf below. Each cluster advances by its OWN width (+gap), so small
  // and disconnected (orphan) clusters sit snug against the bulk instead of being flung a full
  // max-cluster-width away. Largest clusters first so the big model anchors the top-left.
  const targetRowWidth = Math.max(maxClusterWidth, cfg.clustersPerRow * (cfg.nodeWidth + cfg.spacingX) * 3);
  const orderedClusterIds = [...clusterIds].sort((a, b) => {
    const wa = clusterLayouts.get(a)?.width ?? 0;
    const wb = clusterLayouts.get(b)?.width ?? 0;
    return wb - wa; // widest (usually largest) first
  });

  let shelfX = cfg.originX;
  let shelfY = cfg.originY;
  let shelfHeight = 0;
  orderedClusterIds.forEach((clusterId) => {
    const local = clusterLayouts.get(clusterId);
    if (!local) {
      return;
    }
    // wrap to a new shelf if this cluster would overflow the target row width (but always place at
    // least one cluster per shelf)
    if (shelfX > cfg.originX && shelfX + local.width > cfg.originX + targetRowWidth) {
      shelfX = cfg.originX;
      shelfY += shelfHeight + cfg.clusterGapY;
      shelfHeight = 0;
    }
    const baseX = shelfX;
    const baseY = shelfY;

    const localNodes = [...(clusters.get(clusterId) ?? [])].sort((a, b) =>
      (a.target_entity_rdv_name ?? "").localeCompare(b.target_entity_rdv_name ?? "")
    );
    localNodes.forEach((node) => {
      const name = node.target_entity_rdv_name ?? "";
      const slot = local.slots.get(local.keyFor(node)) ?? { x: 0, y: 0 };
      const stableKey = `${clusterId}|${node.target_entity_rdv_id ?? "null"}|${node.source_entity_id ?? "null"}|${name}`;
      positionByNodeKey.set(stableKey, { x: baseX + slot.x, y: baseY + slot.y });
    });

    shelfX += local.width + cfg.clusterGapX;
    shelfHeight = Math.max(shelfHeight, local.height);
  });
  void maxClusterHeight;

  const result = positioned.map((node) => {
    const clusterId = node.synthetic_cluster_id ?? 1;
    const name = node.target_entity_rdv_name ?? "";
    const stableKey = `${clusterId}|${node.target_entity_rdv_id ?? "null"}|${node.source_entity_id ?? "null"}|${name}`;
    const coords = positionByNodeKey.get(stableKey);
    return {
      ...node,
      canvas_x: coords?.x ?? cfg.originX,
      canvas_y: coords?.y ?? cfg.originY
    };
  });

  return result;
}

export function checkNodeOverlaps(
  nodes: DataVaultNode[],
  nodeWidth = DEFAULTS.nodeWidth,
  nodeHeight = DEFAULTS.nodeHeight
) {
  const overlaps: Array<{
    aIndex: number;
    bIndex: number;
    aName: string | null;
    bName: string | null;
  }> = [];

  for (let i = 0; i < nodes.length; i += 1) {
    const a = nodes[i];
    if (a.canvas_x == null || a.canvas_y == null) {
      continue;
    }
    for (let j = i + 1; j < nodes.length; j += 1) {
      const b = nodes[j];
      if (b.canvas_x == null || b.canvas_y == null) {
        continue;
      }

      const noOverlap =
        a.canvas_x + nodeWidth <= b.canvas_x ||
        b.canvas_x + nodeWidth <= a.canvas_x ||
        a.canvas_y + nodeHeight <= b.canvas_y ||
        b.canvas_y + nodeHeight <= a.canvas_y;

      if (!noOverlap) {
        overlaps.push({
          aIndex: i,
          bIndex: j,
          aName: a.target_entity_rdv_name,
          bName: b.target_entity_rdv_name
        });
      }
    }
  }

  return overlaps;
}
